import { achUiLang } from "@/features/achievements/lib/lang";
import type { AchUiLang } from "@/features/achievements/model/types";
import { arabicPluralCategory } from "@/shared/lib/plural/arabicPlural";

/**
 * Числа профиля v2 (PROFILE_CONTRACT §1.4) — таблицей, а не Intl/NumberFormat: данные локалей
 * у браузеров и Android расходятся, а профиль один. Проверка — общие векторы
 * __fixtures__/format_vectors.json (те же у приложения).
 *
 *   язык  тысячи    доля < 1   доля ≥ 1   место
 *   ru    31 200    0,4 %      3 %        5-е
 *   de    31.200    0,4 %      3 %        5.
 *   es    31.200    0,4 %      3 %        5.º
 *   en    31,200    0.4%       3%         5th
 *   zh    31,200    0.4%       3%         第5
 *   ar    31,200    0.4%       3%         5      (арабский интерфейс: западные цифры, как ar-u-nu-latn)
 *
 * Пробелы в ru/de/es — неразрывные (U+00A0). Язык — язык сайта (cn → zh, незнакомый → en);
 * ar — только при включённом арабском интерфейсе (achUiLang), иначе /ar — en, как сегодня.
 */
export type ProfileLang = AchUiLang;

export const profileLang = (siteLang: string | null | undefined): ProfileLang => achUiLang(siteLang);

const NBSP = " ";

const THOUSANDS: Record<ProfileLang, string> = { ru: NBSP, de: ".", es: ".", en: ",", zh: ",", ar: "," };
const DECIMAL: Record<ProfileLang, string> = { ru: ",", de: ",", es: ",", en: ".", zh: ".", ar: "." };
const PERCENT: Record<ProfileLang, string> = { ru: `${NBSP}%`, de: `${NBSP}%`, es: `${NBSP}%`, en: "%", zh: "%", ar: "%" };

const group = (digits: string, sep: string): string => digits.replace(/\B(?=(\d{3})+(?!\d))/g, sep);

/** Целое с разрядами: 31 200 · 31.200 · 31,200 (от 1000 — во всех языках, и в es: 9.000). */
export const formatInt = (n: number, lang: ProfileLang): string => {
    const v = Math.round(Number.isFinite(n) ? n : 0);
    const s = group(String(Math.abs(v)), THOUSANDS[lang]);
    return v < 0 ? `−${s}` : s;
};

/** Половина — вверх (2.5 → 3), без ошибок двоичной записи (0.35·10 = 3.4999…). */
const roundHalfUp = (x: number, digits = 0): number => {
    const k = 10 ** digits;
    return Math.floor(x * k + 0.5 + 1e-9) / k;
};

/** Число доли без знака: меньше 1 — с одним знаком (0,4), иначе целое (3). */
export const formatShareNumber = (share: number, lang: ProfileLang): string => {
    if (share < 1) {
        const v = roundHalfUp(Math.max(0.1, share), 1);
        if (v < 1) return v.toFixed(1).replace(".", DECIMAL[lang]);
    }
    return formatInt(roundHalfUp(share), lang);
};

/** «0,4 %» · «3 %» · «0.4%» — доля участников. */
export const formatShare = (share: number, lang: ProfileLang): string => `${formatShareNumber(share, lang)}${PERCENT[lang]}`;

/** «35 %» — целый процент (от максимума шкалы, «Сильнее N %», «в топ-N %»). */
export const formatPct = (pct: number, lang: ProfileLang): string => `${formatInt(pct, lang)}${PERCENT[lang]}`;

/** Знак процента языка (с неразрывным пробелом перед ним в ru/de/es). */
export const percentSuffix = (lang: ProfileLang): string => PERCENT[lang];

const enOrdinal = (n: number): string => {
    const mod100 = n % 100;
    if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
    const mod10 = n % 10;
    if (mod10 === 1) return `${n}st`;
    if (mod10 === 2) return `${n}nd`;
    if (mod10 === 3) return `${n}rd`;
    return `${n}th`;
};

/** Место: «5-е» · «5.» · «5.º» · «5th» · «第5» · ar — просто число («المركز 5»: слово — в тексте). */
export const formatOrdinal = (n: number, lang: ProfileLang): string => {
    switch (lang) {
        case "ar":
            return String(n);
        case "ru":
            return `${n}-е`;
        case "de":
            return `${n}.`;
        case "es":
            return `${n}.º`;
        case "zh":
            return `第${n}`;
        default:
            return enOrdinal(n);
    }
};

export type PluralCategory = "zero" | "one" | "two" | "few" | "many" | "other";

/**
 * Категория множественного числа (тоже таблицей): ru one/few/many, de/es/en one/other, zh other,
 * ar — все шесть (zero, one, two, few, many, other; shared/lib/plural/arabicPlural).
 */
export const pluralCategory = (n: number, lang: ProfileLang): PluralCategory => {
    const v = Math.abs(Math.trunc(n));
    if (lang === "zh") return "other";
    if (lang === "ar") return arabicPluralCategory(v);
    if (lang !== "ru") return v === 1 ? "one" : "other";
    const mod10 = v % 10;
    const mod100 = v % 100;
    if (mod10 === 1 && mod100 !== 11) return "one";
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return "few";
    return "many";
};

/** Слово по числу: forms — {one, few, many, other} (у ar ещё zero, two); нет нужной формы — other. */
export const plural = (n: number, lang: ProfileLang, forms: Partial<Record<PluralCategory, string>>): string =>
    forms[pluralCategory(n, lang)] ?? forms.other ?? forms.many ?? "";

const ROMAN = ["I", "II", "III", "IV", "V", "VI"];

/** Ступень римской цифрой во всех языках: I–IV. */
export const formatTier = (tier: number): string => ROMAN[tier - 1] || String(tier);
